import { supabase } from './supabase';
import { getMyExtensionSettings, updateExtensionSetting } from './extensionSettings';

export interface UserExtension {
  id?: string;
  user_id: string;
  extension_id: string;
  activated: boolean;
  activated_at?: string;
  deactivated_at?: string | null;
  settings: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

export interface ExtensionSettingsUpdate {
  [settingKey: string]: any;
}

/**
 * Get all activated extensions for the current user
 */
export async function getUserExtensions(): Promise<UserExtension[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to view extensions');
  }

  // Get extension settings from the new RPC
  const extensionSettings = await getMyExtensionSettings();
  
  // Convert to UserExtension format for backward compatibility
  const userExtensions: UserExtension[] = [];
  
  for (const [extensionId, settings] of Object.entries(extensionSettings)) {
    if (settings.enabled) {
      userExtensions.push({
        user_id: user.id,
        extension_id: extensionId,
        activated: true,
        settings: settings,
        activated_at: settings.activated_at || new Date().toISOString()
      });
    }
  }
  
  return userExtensions;
}

/**
 * Get a specific extension for the current user
 */
export async function getUserExtension(extensionId: string): Promise<UserExtension | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to view extension');
  }

  const extensionSettings = await getMyExtensionSettings();
  const settings = extensionSettings[extensionId];
  
  if (!settings || !settings.enabled) {
    return null;
  }
  
  return {
    user_id: user.id,
    extension_id: extensionId,
    activated: true,
    settings: settings,
    activated_at: settings.activated_at || new Date().toISOString()
  };
}

/**
 * Activate an extension for the current user
 */
export async function activateExtension(extensionId: string): Promise<UserExtension> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to activate extensions');
  }

  // Check if extension exists and is not paid (or user has access to paid)
  const { data: extensionData, error: catalogError } = await supabase
    .from('extensions_catalog')
    .select('*')
    .eq('extension_id', extensionId)
    .single();

  if (catalogError || !extensionData) {
    throw new Error('Extension not found');
  }

  // TODO: Add payment validation here
  if (extensionData.is_paid) {
    // For now, we'll allow activation but in the future check payment status
    console.warn('Activating paid extension - payment validation not yet implemented');
  }

  // Check whitelist via server-side RPC (throws error if not whitelisted)
  const { data: whitelistData, error: whitelistError } = await supabase.rpc(
    'activate_extension_with_whitelist',
    { p_extension_id: extensionId }
  );

  if (whitelistError) {
    // Check for specific error types
    if (whitelistError.message?.includes('NO_RUMBLE_API')) {
      const error = new Error('NO_RUMBLE_API');
      (error as any).isRumbleApiError = true;
      throw error;
    }
    if (whitelistError.message?.includes('NOT_WHITELISTED')) {
      const error = new Error('NOT_WHITELISTED');
      (error as any).isWhitelistError = true;
      throw error;
    }
    throw whitelistError;
  }

  // Prepare initial settings with defaults from catalog
  const defaultSettings = extensionData.default_settings || {};

  // Extract defaults from settings_schema if available
  const schemaDefaults: Record<string, any> = {};
  if (extensionData.settings_schema?.sections) {
    for (const section of extensionData.settings_schema.sections) {
      for (const field of section.fields || []) {
        if (field.default !== undefined) {
          schemaDefaults[field.id] = field.default;
        }
      }
    }
  }

  const initialSettings = {
    enabled: true,
    activated_at: new Date().toISOString(),
    ...defaultSettings,
    ...schemaDefaults  // Schema defaults override default_settings
  };

  // For chat-bot, ensure we have the critical defaults
  if (extensionId === 'chat-bot') {
    if (!initialSettings['response-mode']) {
      initialSettings['response-mode'] = 'chat';
    }
    if (!initialSettings['selected-voice']) {
      initialSettings['selected-voice'] = '54e3a85ac9594ffa83264b8a494b901b'; // SpongeBob default
    }
    // Note: bot-enabled is now stored in dedicated column (bot_enabled), not in extension_settings
  }

  // Update extension settings to mark as enabled with defaults
  const updatedSettings = await updateExtensionSetting(extensionId, initialSettings);

  // Enable dedicated toggle columns when activating extensions
  if (extensionId === 'chat-bot') {
    await supabase.rpc('update_bot_enabled', { new_value: true });
  } else if (extensionId === 'tts-integration') {
    await supabase.rpc('update_tts_enabled', { new_value: true });
  }

  // Return in UserExtension format
  return {
    user_id: user.id,
    extension_id: extensionId,
    activated: true,
    settings: updatedSettings[extensionId] || {},
    activated_at: new Date().toISOString()
  };
}

/**
 * Deactivate an extension for the current user
 */
export async function deactivateExtension(extensionId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to deactivate extensions');
  }

  // Get current settings to preserve other data
  const extensionSettings = await getMyExtensionSettings();
  const currentSettings = extensionSettings[extensionId] || {};
  
  // Update to mark as disabled but preserve other settings
  await updateExtensionSetting(extensionId, {
    ...currentSettings,
    enabled: false,
    deactivated_at: new Date().toISOString()
  });
}

/**
 * Update extension settings for the current user
 */
export async function updateExtensionSettings(
  extensionId: string, 
  settingsUpdate: ExtensionSettingsUpdate
): Promise<UserExtension> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to update extension settings');
  }

  // Get current extension settings
  const extensionSettings = await getMyExtensionSettings();
  const currentSettings = extensionSettings[extensionId];
  
  if (!currentSettings || !currentSettings.enabled) {
    throw new Error('Extension not found or not activated');
  }

  // Merge and update settings
  const mergedSettings = {
    ...currentSettings,
    ...settingsUpdate
  };

  const updatedSettings = await updateExtensionSetting(extensionId, mergedSettings);

  return {
    user_id: user.id,
    extension_id: extensionId,
    activated: true,
    settings: updatedSettings[extensionId] || {},
    activated_at: currentSettings.activated_at || new Date().toISOString()
  };
}

/**
 * Get extension settings for a specific extension
 */
export async function getExtensionSettings(extensionId: string): Promise<Record<string, any>> {
  const extensionSettings = await getMyExtensionSettings();
  return extensionSettings[extensionId] || {};
}

/**
 * Delete an extension completely (remove from user's account)
 */
export async function removeExtension(extensionId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to remove extensions');
  }

  // Get all current settings
  const extensionSettings = await getMyExtensionSettings();
  
  // Remove the extension from settings
  delete extensionSettings[extensionId];
  
  // Update with the modified settings (without the removed extension)
  await supabase.rpc('update_my_extension_settings', {
    new_settings: extensionSettings
  });
}

/**
 * Check if an extension is activated for the current user
 */
export async function isExtensionActivated(extensionId: string): Promise<boolean> {
  const extensionSettings = await getMyExtensionSettings();
  return extensionSettings[extensionId]?.enabled || false;
}

/**
 * Get all activated extensions as a simple array of extension IDs
 */
export async function getActivatedExtensionIds(): Promise<string[]> {
  const extensionSettings = await getMyExtensionSettings();
  
  return Object.entries(extensionSettings)
    .filter(([_, settings]) => settings.enabled)
    .map(([extensionId, _]) => extensionId);
}